import { FormProvider, useForm } from 'react-hook-form';
import {
  AllocationBucketType,
  AllocationDurationType,
  AllocationType,
  AllocationBucketTypes,
  AllocationDurationTypes,
  AllocationTypes,
  ALLOCATION_TYPE_METADATA,
  ALLOCATION_DURATION_METADATA,
  ALLOCATION_BUCKET_METADATA,
} from '@/features/Collaborators/Models';
import { useEffect, useMemo } from 'react';
import { RadioGroup } from '@headlessui/react';
import clsx from 'clsx';
import { CustomDatePicker } from '@/components/shared/inputs/CustomDatePicker';
import { format, parse } from 'date-fns';
import { ClientCombobox } from '@/components/shared/ClientCombobox';
import { CollaboratorCombobox } from '@/components/shared/CollaboratorCombobox';
import { CurrencyInput } from '@/components/shared';
import { useResourceOptions } from '@/hooks/useResourceOptions';
import type { Resource } from '@/features/Collaborators/Models/collaborator';
import {
  ALLOCATION_BUCKET_ICON_MAP,
  ALLOCATION_TYPE_ICON_MAP,
} from '@/features/Collaborators/Allocations/constants/allocationTypeIcons';
import { NoticeReasonField } from './NoticeReasonField';

export interface CollaboratorAllocationFormValues {
  allocationType: AllocationType;
  durationType: AllocationDurationType;
  bucketType: AllocationBucketType;
  bucket: number;
  startDate: Date | null;
  endDate: Date | null;
  clientId: string | null;
  clientName: string | null;
  clientLogo: string | null;
  clientResponsible: string | null;
  rate: number | null;
  responsibleId: string | null;
  responsibleName: string | null;
  responsibleAvatar: string | null;
  departmentId: string | null;
  departmentName: string | null;
  projectId: string | null;
  projectName: string | null;
  capacityId: string | null;
  capacityLabel: string | null;
  capacityTypeId: string | null;
  capacityTypeName: string | null;
  capacityTechnologyId: string | null;
  capacityTechnologyName: string | null;
  capacitySeniorityId: string | null;
  capacitySeniorityName: string | null;
  noticeReason: string | null;
}

const DEFAULT_VALUES: CollaboratorAllocationFormValues = {
  allocationType: AllocationTypes.BodyShop,
  durationType: AllocationDurationTypes.Undefined,
  bucketType: AllocationBucketTypes.Monthly,
  bucket: 168,
  startDate: new Date(),
  endDate: null,
  clientId: null,
  clientName: null,
  clientLogo: null,
  clientResponsible: null,
  rate: null,
  responsibleId: null,
  responsibleName: null,
  responsibleAvatar: null,
  departmentId: null,
  departmentName: null,
  projectId: null,
  projectName: null,
  capacityId: null,
  capacityLabel: null,
  capacityTypeId: null,
  capacityTypeName: null,
  capacityTechnologyId: null,
  capacityTechnologyName: null,
  capacitySeniorityId: null,
  capacitySeniorityName: null,
  noticeReason: null,
};

const durationTypeCards = (Object.keys(ALLOCATION_DURATION_METADATA) as AllocationDurationType[]).map(
  (key) => ({
    value: key,
    label: ALLOCATION_DURATION_METADATA[key].label,
    description: ALLOCATION_DURATION_METADATA[key].description,
  })
);

const bucketTypeCards = (
  [
    AllocationBucketTypes.Monthly,
    AllocationBucketTypes.Defined,
    AllocationBucketTypes.Weekly,
    AllocationBucketTypes.Daily,
  ] as AllocationBucketType[]
).map((key) => ({
  value: key,
  label: ALLOCATION_BUCKET_METADATA[key].label,
  description: ALLOCATION_BUCKET_METADATA[key].description,
}));

export interface CollaboratorAllocationFormProps {
  collaboratorId: string;
  collaboratorResources?: Resource[] | null;
  onSubmit: (values: CollaboratorAllocationFormValues) => Promise<void> | void;
  onCancel: () => void;
  isLoading?: boolean;
  initialValues?: CollaboratorAllocationFormValues | null;
  mode?: 'create' | 'edit';
  terminationAppliedAt?: Date | null;
}

export function CollaboratorAllocationForm({
  collaboratorId,
  collaboratorResources = [],
  onSubmit,
  onCancel,
  isLoading = false,
  initialValues = null,
  mode = 'create',
  terminationAppliedAt = null,
}: CollaboratorAllocationFormProps) {
  const methods = useForm<CollaboratorAllocationFormValues>({
    mode: 'onChange',
    defaultValues: initialValues ?? DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  useEffect(() => {
    if (initialValues) {
      methods.reset(initialValues);
    } else {
      methods.reset(DEFAULT_VALUES);
    }
  }, [initialValues, methods]);

  const selectedAllocationType = methods.watch('allocationType');
  const selectedDurationType = methods.watch('durationType');
  const startDateValue = methods.watch('startDate');
  const endDateValue = methods.watch('endDate');
  const selectedBucketType = methods.watch('bucketType');
  const bucketValue = methods.watch('bucket');
  const selectedClientId = methods.watch('clientId');
  const selectedCapacityId = methods.watch('capacityId') ?? null;
  // Só usados pra reconstruir a capacidade já salva quando ela não bate mais com o
  // cadastro atual do colaborador (ver capacityOptions abaixo) — não editados diretamente.
  const selectedCapacityLabel = methods.watch('capacityLabel') ?? null;
  const selectedCapacityTypeId = methods.watch('capacityTypeId') ?? null;
  const selectedCapacityTypeName = methods.watch('capacityTypeName') ?? null;
  const selectedCapacityTechnologyId = methods.watch('capacityTechnologyId') ?? null;
  const selectedCapacityTechnologyName = methods.watch('capacityTechnologyName') ?? null;
  const selectedCapacitySeniorityId = methods.watch('capacitySeniorityId') ?? null;
  const selectedCapacitySeniorityName = methods.watch('capacitySeniorityName') ?? null;

  const {
    types: resourceTypes,
    technologies,
    seniorities,
    loading: loadingResourceInputs,
    error: resourceInputsError,
  } = useResourceOptions();

  const resourceTypeMap = useMemo(() => new Map(resourceTypes.map((type) => [type.id, type.name])), [resourceTypes]);
  const technologyMap = useMemo(() => new Map(technologies.map((tech) => [tech.id, tech.name])), [technologies]);
  const seniorityMap = useMemo(() => new Map(seniorities.map((seniority) => [seniority.id, seniority.name])), [seniorities]);

  const collaboratorResourceList = useMemo(
    () => (collaboratorResources ?? []).filter((resource): resource is Resource => Boolean(resource)),
    [collaboratorResources]
  );

  const capacityOptions = useMemo(() => {
    const derivadas = collaboratorResourceList
      .filter((resource) => resource.typeId && resource.technologyId && resource.seniorityId)
      .map((resource) => {
        const typeId = resource.typeId ?? null;
        const technologyId = resource.technologyId ?? null;
        const seniorityId = resource.seniorityId ?? null;

        const typeName = resource.resourceTypeName ?? (typeId ? resourceTypeMap.get(typeId) ?? null : null);
        const technologyName = resource.technologyName ?? (technologyId ? technologyMap.get(technologyId) ?? null : null);
        const seniorityName = resource.seniorityName ?? (seniorityId ? seniorityMap.get(seniorityId) ?? null : null);

        const titleParts = [technologyName ?? technologyId ?? '', typeName ?? typeId ?? ''].filter(Boolean);
        const title = titleParts.join(' • ') || 'Capacidade';
        const subtitle = seniorityName ?? null;
        const label = subtitle ? `${title} (${subtitle})` : title;
        const id = `${typeId ?? 'type'}|${technologyId ?? 'technology'}|${seniorityId ?? 'seniority'}`;

        return {
          id,
          title,
          subtitle,
          label,
          typeId,
          typeName: typeName ?? null,
          technologyId,
          technologyName: technologyName ?? null,
          seniorityId,
          seniorityName: seniorityName ?? null,
          isPrimary: Boolean(resource.isPrimary),
        };
      });

    // A alocação já tinha uma capacidade salva (id é uma combinação tipo|tecnologia|
    // senioridade calculada na hora) que não existe mais entre as capacidades atuais do
    // colaborador — perfil dele mudou depois que a alocação foi criada. Antes disso ficava
    // invisível: o campo era limpo em silêncio (ver useEffect abaixo) e o botão de salvar
    // ficava desabilitado sem nenhum aviso. Mantém como opção extra, com os dados que já
    // tinha salvos, pra não perder a seleção nem travar o formulário.
    const capacidadeSalvaAusente = selectedCapacityId && !derivadas.some((opcao) => opcao.id === selectedCapacityId);
    if (!capacidadeSalvaAusente) {
      return derivadas;
    }

    const tituloParts = [selectedCapacityTechnologyName, selectedCapacityTypeName].filter(Boolean);
    const titulo = tituloParts.join(' • ') || selectedCapacityLabel || 'Capacidade';

    return [
      ...derivadas,
      {
        id: selectedCapacityId,
        title: titulo,
        subtitle: selectedCapacitySeniorityName
          ? `${selectedCapacitySeniorityName} — não está mais no cadastro atual do colaborador`
          : 'Não está mais no cadastro atual do colaborador',
        label: selectedCapacityLabel ?? titulo,
        typeId: selectedCapacityTypeId,
        typeName: selectedCapacityTypeName,
        technologyId: selectedCapacityTechnologyId,
        technologyName: selectedCapacityTechnologyName,
        seniorityId: selectedCapacitySeniorityId,
        seniorityName: selectedCapacitySeniorityName,
        isPrimary: false,
      },
    ];
  }, [
    collaboratorResourceList,
    resourceTypeMap,
    technologyMap,
    seniorityMap,
    selectedCapacityId,
    selectedCapacityLabel,
    selectedCapacityTypeId,
    selectedCapacityTypeName,
    selectedCapacityTechnologyId,
    selectedCapacityTechnologyName,
    selectedCapacitySeniorityId,
    selectedCapacitySeniorityName,
  ]);

  const clearCapacityFields = () => {
    methods.setValue('capacityId', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityLabel', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTypeId', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTypeName', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTechnologyId', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTechnologyName', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacitySeniorityId', null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacitySeniorityName', null, { shouldDirty: true, shouldTouch: true });
  };

  const handleCapacitySelect = (capacityKey: string) => {
    const option = capacityOptions.find((item) => item.id === capacityKey);
    if (!option) {
      clearCapacityFields();
      return;
    }

    methods.setValue('capacityId', option.id, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityLabel', option.label, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTypeId', option.typeId ?? null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTypeName', option.typeName ?? null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTechnologyId', option.technologyId ?? null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacityTechnologyName', option.technologyName ?? null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacitySeniorityId', option.seniorityId ?? null, { shouldDirty: true, shouldTouch: true });
    methods.setValue('capacitySeniorityName', option.seniorityName ?? null, { shouldDirty: true, shouldTouch: true });
  };

  useEffect(() => {
    if (!selectedCapacityId) {
      return;
    }

    const exists = capacityOptions.some((option) => option.id === selectedCapacityId);
    if (!exists) {
      clearCapacityFields();
    }
  }, [selectedCapacityId, capacityOptions]);

  const handleAllocationTypeChange = (value: AllocationType) => {
    methods.setValue('allocationType', value, {
      shouldDirty: true,
      shouldTouch: true,
    });
    if (value === AllocationTypes.Product) {
      methods.setValue('durationType', AllocationDurationTypes.Defined, { shouldDirty: true });
    }
  };

  const handleDurationTypeChange = (value: AllocationDurationType) => {
    methods.setValue('durationType', value, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleBucketTypeChange = (value: AllocationBucketType) => {
    methods.setValue('bucketType', value, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleStartDateChange = (value: string) => {
    const parsed = parse(value, 'dd/MM/yyyy', new Date());
    if (Number.isNaN(parsed.getTime())) {
      methods.setValue('startDate', null, { shouldDirty: true });
    } else {
      methods.setValue('startDate', parsed, { shouldDirty: true });
    }
  };

  const handleEndDateChange = (value: string) => {
    const parsed = parse(value, 'dd/MM/yyyy', new Date());
    if (Number.isNaN(parsed.getTime())) {
      methods.setValue('endDate', null, { shouldDirty: true });
    } else {
      methods.setValue('endDate', parsed, { shouldDirty: true });
    }
  };

  const allocationTypeCards = useMemo(
    () =>
      (Object.keys(ALLOCATION_TYPE_METADATA) as AllocationType[]).map((key) => ({
        value: key,
        label: ALLOCATION_TYPE_METADATA[key].label,
        description: ALLOCATION_TYPE_METADATA[key].description,
        icon: ALLOCATION_TYPE_ICON_MAP[key],
      })),
    []
  );

  const isNoticeAllocation = selectedAllocationType === AllocationTypes.Product;
  const noticeReasonValue = methods.watch('noticeReason');

  const submitDisabled = useMemo(
    () =>
      isSubmitting ||
      isLoading ||
      (mode === 'create' && !isDirty) ||
      !selectedCapacityId ||
      (isNoticeAllocation && !noticeReasonValue?.trim()),
    [isSubmitting, isLoading, isDirty, mode, selectedCapacityId, isNoticeAllocation, noticeReasonValue]
  );

  const formTitle = mode === 'edit' ? 'Editar Alocação' : 'Registrar Alocação';
  const formDescription =
    mode === 'edit'
      ? 'Atualize as informações desta alocação.'
      : 'Defina as informações da alocação para este colaborador.';
  const submitLabel = mode === 'edit' ? 'Atualizar Alocação' : 'Salvar Alocação';

  const startDateFormatted = startDateValue
    ? format(startDateValue, 'dd/MM/yyyy')
    : '';
  const endDateFormatted = endDateValue
    ? format(endDateValue, 'dd/MM/yyyy')
    : '';
  const bucketInputValue =
    bucketValue === null || bucketValue === undefined ? '' : String(bucketValue);
  const rateValue = methods.watch('rate');

  const handleFormSubmit = handleSubmit(async (values) => {
    if (values.allocationType === AllocationTypes.Product) {
      if (!values.noticeReason?.trim()) {
        methods.setError('noticeReason', { message: 'Informe o motivo do aviso prévio.' });
        return;
      }
      if (!values.endDate) {
        methods.setError('endDate', { message: 'Informe a data de término do aviso prévio.' });
        return;
      }
    }
    await onSubmit(values);
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="border-b border-gray-200 px-4 py-5 sm:px-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                {formTitle}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {formDescription}
              </p>
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6">
            <section className="space-y-8">
              <header>
                <h3 className="text-sm font-medium text-gray-900">
                  Tipo de alocação
                </h3>
                <p className="text-sm text-gray-500">
                  Escolha o modelo que melhor descreve o trabalho realizado pelo colaborador.
                </p>
              </header>

              <RadioGroup
                value={selectedAllocationType}
                onChange={handleAllocationTypeChange}
                className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
              >
                {allocationTypeCards.map(({ value, label, description, icon: Icon }) => (
                  <RadioGroup.Option key={value} value={value}>
                    {({ checked }) => (
                      <div
                        className={clsx(
                          'relative flex h-full cursor-pointer rounded-xl border p-4 transition focus:outline-none',
                          checked
                            ? 'border-primary-500 bg-primary-50 shadow-sm'
                            : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex w-full items-start gap-3">
                          <div
                            className={clsx(
                              'rounded-lg p-2',
                              checked
                                ? 'bg-primary-500 text-white'
                                : 'bg-gray-100 text-primary-500'
                            )}
                          >
                            <Icon className="h-5 w-5" aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {label}
                            </h4>
                            <p className="mt-1 text-sm text-gray-500">{description}</p>
                          </div>
                        </div>
                        <span
                          className={clsx(
                            'pointer-events-none absolute inset-0 rounded-xl ring-2 ring-primary-500',
                            checked ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </div>
                    )}
                  </RadioGroup.Option>
                ))}
              </RadioGroup>

              <div className="space-y-3">
                <header>
                  <h3 className="text-sm font-medium text-gray-900">
                    Duração da alocação
                  </h3>
                  <p className="text-sm text-gray-500">
                    Informe se a alocação possui data final definida.
                  </p>
                </header>

                <RadioGroup
                  value={selectedDurationType}
                  onChange={handleDurationTypeChange}
                  className="grid gap-4 sm:grid-cols-2"
                >
                  {durationTypeCards.map(({ value, label, description }) => (
                    <RadioGroup.Option key={value} value={value}>
                      {({ checked }) => (
                        <div
                          className={clsx(
                            'h-full cursor-pointer rounded-xl border p-4 transition focus:outline-none',
                            checked
                              ? 'border-primary-500 bg-primary-50 shadow-sm'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                          )}
                        >
                          <h4 className="text-sm font-semibold text-gray-900">
                            {label}
                          </h4>
                          <p className="mt-1 text-sm text-gray-500">{description}</p>
                        </div>
                      )}
                    </RadioGroup.Option>
                  ))}
                </RadioGroup>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="allocation-start-date"
                      className="text-sm font-medium text-gray-700"
                    >
                      Data de início
                    </label>
                    <CustomDatePicker
                      id="allocation-start-date"
                      value={startDateFormatted}
                      onChange={handleStartDateChange}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="allocation-end-date"
                      className="text-sm font-medium text-gray-700"
                    >
                      Data de término
                    </label>
                    <CustomDatePicker
                      id="allocation-end-date"
                      value={endDateFormatted}
                      onChange={handleEndDateChange}
                      placeholder="dd/mm/aaaa"
                    />
                    {methods.formState.errors.endDate && (
                      <p className="mt-1 text-sm text-red-600">{methods.formState.errors.endDate.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {isNoticeAllocation && (
                <div className="space-y-3">
                  {terminationAppliedAt ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      Desligamento aplicado em {format(terminationAppliedAt, 'dd/MM/yyyy')}. Alterar esta alocação não muda a ficha do colaborador.
                    </div>
                  ) : (
                    <NoticeReasonField endDate={endDateValue} />
                  )}
                </div>
              )}

              <div className="space-y-3">
                <header>
                  <h3 className="text-sm font-medium text-gray-900">Capacidade prevista</h3>
                  <p className="text-sm text-gray-500">
                    Selecione como as horas serão contabilizadas e informe o total previsto para a alocação.
                  </p>
                </header>

                <RadioGroup
                  value={selectedBucketType}
                  onChange={handleBucketTypeChange}
                  className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
                >
                  {bucketTypeCards.map(({ value, label, description }) => {
                    const Icon = ALLOCATION_BUCKET_ICON_MAP[value];
                    return (
                      <RadioGroup.Option key={value} value={value}>
                        {({ checked }) => (
                          <div
                            className={clsx(
                              'relative flex h-full cursor-pointer rounded-xl border p-4 transition focus:outline-none',
                              checked
                                ? 'border-primary-500 bg-primary-50 shadow-sm'
                                : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                            )}
                          >
                            <div className="flex w-full items-start gap-3">
                              <div
                                className={clsx(
                                  'rounded-lg p-2',
                                  checked
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-gray-100 text-primary-500'
                                )}
                              >
                                <Icon className="h-5 w-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
                                <p className="mt-1 text-sm text-gray-500">{description}</p>
                              </div>
                            </div>
                            <span
                              className={clsx(
                                'pointer-events-none absolute inset-0 rounded-xl ring-2 ring-primary-500',
                                checked ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        )}
                      </RadioGroup.Option>
                    );
                  })}
                </RadioGroup>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="allocation-bucket"
                      className="text-sm font-medium text-gray-700"
                    >
                      Horas previstas
                    </label>
                    <input
                      id="allocation-bucket"
                      type="number"
                      min={0}
                      className="input-primary mt-1 w-full"
                      value={bucketInputValue}
                      onChange={(event) => {
                        const raw = event.target.value;
                        const parsed = raw === '' ? 0 : Number(raw);
                        methods.setValue('bucket', Number.isNaN(parsed) ? 0 : parsed, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="allocation-rate" className="text-sm font-medium text-gray-700">
                      Taxa
                    </label>
                    <CurrencyInput
                      id="allocation-rate"
                      name="allocation-rate"
                      label=""
                      value={rateValue ?? undefined}
                      onValueChange={(formatted, _name, values) => {
                        if (!formatted) {
                          methods.setValue('rate', null, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          return;
                        }

                        const numericValue = values?.float ?? null;
                        methods.setValue('rate', numericValue, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      placeholder="0,00"
                      disabled={isSubmitting || isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3">
                  <header>
                    <h3 className="text-sm font-medium text-gray-900">Cliente</h3>
                    <p className="text-sm text-gray-500">
                      Identifique o cliente associado a esta alocação.
                    </p>
                  </header>

                  <ClientCombobox
                    selectedId={selectedClientId ?? undefined}
                    onChange={(client) => {
                      methods.setValue('clientId', client?.id ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      methods.setValue('clientName', client?.name ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      methods.setValue('clientLogo', client?.logoUrl ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>

                <div className="space-y-3">
                  <header>
                    <h3 className="text-sm font-medium text-gray-900">Responsável Raro</h3>
                    <p className="text-sm text-gray-500">
                      Escolha quem será o ponto focal dentro da Raro Labs para esta alocação.
                    </p>
                  </header>

                  <CollaboratorCombobox
                    selectedId={methods.watch('responsibleId') ?? undefined}
                    onChange={(collaborator) => {
                      methods.setValue('responsibleId', collaborator?.id ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      methods.setValue('responsibleName', collaborator?.name ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      methods.setValue('responsibleAvatar', collaborator?.photoUrl ?? null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="allocation-initiative" className="text-sm font-medium text-gray-700">
                    Iniciativa
                  </label>
                  <input
                    id="allocation-initiative"
                    type="text"
                    className="input-primary mt-1 w-full"
                    placeholder="Informe o Projeto, Nome da Squad ou Time"
                    value={methods.watch('projectName') ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      methods.setValue('projectName', value || null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="allocation-client-responsible"
                    className="text-sm font-medium text-gray-700"
                  >
                    Responsável Cliente
                  </label>
                  <input
                    id="allocation-client-responsible"
                    type="text"
                    className="input-primary mt-1 w-full"
                    placeholder="Nome do responsável do cliente"
                    value={methods.watch('clientResponsible') ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      methods.setValue('clientResponsible', value || null, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <header>
                  <h3 className="text-sm font-medium text-gray-900">Papel</h3>
                  <p className="text-sm text-gray-500">
                    Escolha a capacidade cadastrada no perfil deste colaborador para associá-la à alocação.
                  </p>
                </header>

                {resourceInputsError ? (
                  <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    Não foi possível carregar as capacidades disponíveis. Tente novamente mais tarde.
                  </p>
                ) : loadingResourceInputs ? (
                  <p className="text-sm text-gray-500">Carregando capacidades...</p>
                ) : capacityOptions.length === 0 ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                    Este colaborador ainda não possui capacidades cadastradas. Atualize o cadastro para prosseguir.
                  </p>
                ) : (
                  <RadioGroup
                    value={selectedCapacityId}
                    onChange={handleCapacitySelect}
                    className="grid gap-3 md:grid-cols-2"
                    disabled={isSubmitting || isLoading}
                  >
                    {capacityOptions.map((option) => (
                      <RadioGroup.Option
                        key={option.id}
                        value={option.id}
                        className={({ checked }) =>
                          clsx(
                            'relative rounded-xl border px-4 py-3 text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                            checked
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-primary-300'
                          )
                        }
                      >
                        {({ checked }) => (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <RadioGroup.Label as="p" className="text-sm font-semibold text-gray-900">
                                  {option.title}
                                </RadioGroup.Label>
                                {option.subtitle && (
                                  <RadioGroup.Description as="p" className="text-xs text-gray-500">
                                    {option.subtitle}
                                  </RadioGroup.Description>
                                )}
                              </div>
                              {option.isPrimary && (
                                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                  Principal
                                </span>
                              )}
                            </div>
                            <span
                              className={clsx(
                                'pointer-events-none absolute inset-0 rounded-xl ring-2 ring-primary-500',
                                checked ? 'opacity-100' : 'opacity-0'
                              )}
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </RadioGroup>
                )}
              </div>
            </section>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary order-2 sm:order-1"
              disabled={isSubmitting || isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary order-1 sm:order-2"
              disabled={submitDisabled}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export default CollaboratorAllocationForm;
